import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, TableCell } from '@mui/material';
import styles from './TimeClockList.module.scss';
import classNames from 'classnames/bind';
import { ITimeClockEvent } from '~/utils/interface/timeClock';
import BaseTable from '~/components/common/base-table/BaseTable';
import { BaseImage, LabelTimeHour } from '~/components/common';
import logo from '~/assets/images/logo.png';
import dayjs from 'dayjs';
import { MenuVerticalIcon } from '~/assets/icons';
import MenuOption from '~/components/jobs/menu-option/MenuOption';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetTimeClocks } from '~/thunks/time-clocks/timeClocksThunk';
import { ITimeclockFilter, timeClocksActions } from '~/thunks/time-clocks/timeClocksSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { startCase } from 'lodash';
type TimeClockListProps = {
  onOpenEdit: (id: string) => void;
};

const cx = classNames.bind(styles);
const headCells: Array<HeadCelType> = [
  {
    id: 'account',
    sortable: true,
    searchable: true,
  },
  {
    id: 'date',
    sortable: true,
    searchable: true,
  },
  {
    id: 'task',
    sortable: true,
    searchable: true,
  },

  {
    id: 'project',
    sortable: true,
    searchable: true,
  },
  {
    id: 'time',
    sortable: true,
    searchable: true,
  },
  {
    id: 'checkIn',
    sortable: true,
    searchable: false,
  },
  {
    id: 'checkOut',
    sortable: true,
    searchable: false,
  },
  {
    id: 'location',
    sortable: true,
    searchable: false,
  },
  {
    id: 'status',
    sortable: true,
    searchable: false,
  },
  {
    id: 'action',
    sortable: false,
    searchable: false,
  },
];

const TimeClockList = (props: TimeClockListProps) => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const permissions = usePermission();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { timeClocks, totalPages, currentPage, needRefreshData, filter } = useAppSelector((state) => state.timeClocks);

  //#endregion Selector

  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const fetchTimeClocks = useCallback(
    (filter: ITimeclockFilter) => {
      dispatch(handleGetTimeClocks(filter));
    },
    [dispatch]
  );
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    fetchTimeClocks(filter);
  }, [fetchTimeClocks, filter]);

  useEffect(() => {
    if (needRefreshData) {
      fetchTimeClocks({
        ...filter,
        page: currentPage,
      });
    }
  }, [fetchTimeClocks, needRefreshData, filter, currentPage]);
  //#endregion Implement Hook

  //#region Handle Function
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, timeClockEventId?: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(timeClockEventId);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(timeClocksActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(timeClocksActions.changeMenuSelectId(menuSelectedId));
      dispatch(timeClocksActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    fetchTimeClocks({
      ...filter,
      page: newPage,
    });
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      timeClocksActions.setFilterProperty({
        key: 'sort',
        value: sortBy.map((item) => ({ field: item.orderBy, type: item.order })),
      })
    );
  };

  const { t } = useTranslation('admin/time-clocks');

  const headCellsWithLabel: Array<HeadCelType> = useMemo(() => {
    return headCells.map((headCell) => ({ ...headCell, label: t(`properties.${headCell.id}`) }));
  }, [t]);

  const renderStatusComponent = useCallback(
    (status: string) => {
      return (
        <Box component='span' className={cx('status-info', status)}>
          {t(`statuses.${status}`)}
        </Box>
      );
    },
    [t]
  );
  //#endregion Handle Function
  return (
    <Box className={cx('time-clock-list-container')}>
      <BaseTable<ITimeClockEvent>
        data={timeClocks}
        setSelectedIds={setSelectedIds}
        headCells={headCellsWithLabel}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        onRequestSort={handleRequestSort}
        onChangePage={handleChangePage}
        renderCell={(timeClockEvent) => {
          return (
            <>
              <TableCell component='th'>
                <Box className={cx('user-container')}>
                  <BaseImage src={timeClockEvent.account?.avatar ?? logo} className={cx('image')} />
                  <Box className={cx('user-name')}>{timeClockEvent.account?.companyAccount?.name}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box>{dayjs(timeClockEvent.date).format('DD MMMM YYYY')}</Box>
              </TableCell>

              <TableCell>{timeClockEvent.task && <Box>{startCase(timeClockEvent.task.name)}</Box>}</TableCell>

              <TableCell>{timeClockEvent.job && <Box>{timeClockEvent.job.name}</Box>}</TableCell>

              <TableCell>
                <Box>{<LabelTimeHour zeroValue='-' time={timeClockEvent?.totalTimeWork} />}</Box>
              </TableCell>

              <TableCell>
                <Box>{dayjs(timeClockEvent.checkIn).format('hh:mm A')}</Box>
              </TableCell>

              <TableCell>
                <Box>{timeClockEvent.checkOut ? dayjs(timeClockEvent.checkOut).format('hh:mm A') : ''}</Box>
              </TableCell>

              <TableCell>
                <Box>{timeClockEvent.location}</Box>
              </TableCell>

              <TableCell>
                <Box>{renderStatusComponent(timeClockEvent.status)}</Box>
              </TableCell>

              {(permissions[PERMISSIONS.timeClock_edit] || permissions[PERMISSIONS.timeClock_delete]) && (
                <TableCell align={'left'} padding={'none'}>
                  <Box className='flex flex-1 justify-center'>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, timeClockEvent?.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      ></BaseTable>

      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.timeClock_edit]}
        enableDelete={permissions[PERMISSIONS.timeClock_delete]}
      />
    </Box>
  );
};

export default memo(TimeClockList);
